<template>
    <v-row class="ma-0 pa-0" align="center" justify="center">
      <v-col cols="12" class="chart_style ma-0 pa-0">
        <v-chart :id="chart_id" :ref="chart_ref" class="chart" :key="chart_key" :option="option" @legendselectchanged="handleLegendSelectChanged" autoresize></v-chart>
      </v-col>
    </v-row>
  </template>
  
  <script>
  import VChart, { THEME_KEY } from "vue-echarts";
  import "echarts";
  import { use } from "echarts/core";
  import { CanvasRenderer } from "echarts/renderers";
  import { TitleComponent, TooltipComponent, LegendComponent } from "echarts/components";
  import cpalette from "@/configs/ChartColors/get_color_palette";
  
  use([CanvasRenderer, TitleComponent, TooltipComponent, LegendComponent]);
  export default {
    name: "LineChart",
    props: ["chart_title", "chart_data", "chart_id", "chart_ref", "chart_key", "color_palette", "shared_legend_selected"],
    components: {
      VChart,
    },
    provide: {
      [THEME_KEY]: "light",
    },
    data() {
      return { legend_selected: {} };
    },
    computed: {
      option: function () {
        const dataset = Array.isArray(this.chart_data) ? this.chart_data : [];

        if (dataset.length === 0) {
          return {};
        }

        const data_category = [];
        const data_series = dataset[0].slice(1);
        const legendSelected = { ...this.legend_selected, ...this.shared_legend_selected };
        const visibleSeries = data_series.map((name, index) =>
          legendSelected[name] !== false ? index + 1 : null
        ).filter(index => index !== null);
        const rows = dataset.slice(1);
        const hasVisibleData = row => visibleSeries.some(index => row[index] != null);
        const firstVisible = rows.findIndex(hasVisibleData);
        let lastVisible = rows.length - 1;
        while (lastVisible >= firstVisible && lastVisible >= 0 && !hasVisibleData(rows[lastVisible])) {
          lastVisible--;
        }
        const visibleRows = firstVisible < 0 ? [] : rows.slice(firstVisible, lastVisible + 1);
        const data = Array.from(Array(data_series.length), () => []);
        const series = [];

        visibleRows.forEach(row => {
          data_category.push(row[0]);
          data_series.forEach((name, index) => {
            data[index].push(row[index + 1] ?? null);
          });
        });

        for (let idx = 0; idx < data.length; idx++) {
          series.push({
            name: data_series[idx],
            showSymbol: false,
            type: "line",
            smooth: true,
            connectNulls: true,
            data: data[idx],
            emphasis: {
              focus: "series",
            },
          });
        }

        const formatShortNumber = this.formatShortNumber;

        return {
          title: {
            text: this.chart_title,
            fontFamily: "Poppins",
            left: 'center',
          },
          color: this.color_palette ? cpalette.get_color_palette(this.color_palette) : [
            "#70E000", "#A4133C", "#2AB7CA", "#3A0CA3", "#F6BD60", "#1F4E5F", "#F28482",
            "#2F5D50", "#9FE0D0", "#A44A3F", "#CDB4DB", "#D7263D", "#75B9E3", "#6B8E23",
            "#E8B4B8", "#1E3A8A", "#F77F00", "#6D597A", "#A7C957", "#D0006F", "#B7D3E9",
            "#4A6F46", "#F4A261", "#2E5AAC", "#9CAF88", "#7B2CBF", "#C19A6B", "#00B4D8",
            "#C08497", "#2A9D8F", "#E9C46A", "#3A86C8", "#B46A55", "#4EC5C1", "#D4A017",
            "#A2678A", "#D8C3A5", "#C06542", "#B8A1D9", "#D6E6F2"
          ],
          legend: {
            data: data_series,
            selected: legendSelected,
            type: "scroll",
            left: 'center',
            top: '5%',
            width: "90%",
            textStyle: {
                fontFamily: "Poppins",
                fontSize: 12
            }
          },
          grid: {
            top: '10%',
            left: "3%",
            right: "3%",
            bottom: "4%",
            containLabel: true,
          },
          xAxis:{
              type: "category",
              data: data_category,
              axisTick: {
                alignWithLabel: true,
              },
              axisLabel: {
                fontSize: 12,
                fontFamily: "Poppins"
              },
              name: 'Frequency',
              nameLocation: 'middle',
              nameGap: 30,
              nameTextStyle: {
                  fontSize: 14,
                  fontFamily: "Poppins",
                  fontWeight: 'bold'
              }
          },
          yAxis: {
              type: "value",
              axisLabel: {
                fontSize: 12,
                fontFamily: "Poppins",
                formatter: function (value) {
                      return Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
                },
              },
              name: 'Unique Reach',
              nameLocation: 'middle',
              nameGap: 40,
              nameTextStyle: {
                  fontSize: 14,
                  fontFamily: "Poppins",
                  fontWeight: 'bold'
              }
          },
          tooltip: {
              order: 'valueAsc',
              trigger: 'axis',
              formatter: function (params) {
                  const title = params[0].axisValueLabel || params[0].axisValue;

                  const body = params.map(item => {
                      return `${item.marker}${item.seriesName || ''}: ${formatShortNumber(item.value) || ''}`;
                  }).join('<br/>');

                  return `<strong>Freq = ${title}</strong><br/>${body}`;
              }
          },
          dataZoom: [
            {
              type: 'slider',
              show: false,
              xAxisIndex: 0,
              start: 0,
              end: 100
            },
            {
              type: 'inside',
              xAxisIndex: 0,
              start: 0,
              end: 100
            }
          ],
          series: series
        };
      },
    },
    methods: {
      handleLegendSelectChanged(params) {
        this.legend_selected = { ...(params.selected || {}) };
        this.$emit("legend_selection_changed", this.legend_selected);
      },
      formatShortNumber: function (value) {
        const numeric_value = Number(value);

        if (Number.isNaN(numeric_value)) {
          return "";
        }

        if (numeric_value >= 1e9) return (numeric_value / 1e9).toFixed(1) + 'B';
        if (numeric_value >= 1e6) return (numeric_value / 1e6).toFixed(1) + 'M';
        if (numeric_value >= 1e3) return (numeric_value / 1e3).toFixed(1) + 'K';
        return numeric_value.toFixed(0);
      },
    },
  };
  </script>
  
  <style scoped>
  .chart_title {
    font-family: "Poppins" !important;
    font-size: 10pt !important;
    line-height: 40px;
  
    display: block;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chart_style {
    height: 60vh;
    font-family: "Poppins" !important;
  }
  </style>
  
